import React, { useEffect, useState, useRef } from 'react';
import { ensureScriptLoaded } from '../services/providers/ModulesProvider';
import PropTypes from 'prop-types';
import dynamicFederation from './dynamicFederation';

const ExternalPageLoader = ({
  moduleUrl,
  containerName,
  componentPath,
  children,
  props = {},
  onLoadingChange,
}) => {
  const [Component, setComponent] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const onLoadingChangeRef = useRef(onLoadingChange);
  /** Evita que callbacks de cargas anteriores atualizem o loading após navegação (race condition). */
  const loadIdRef = useRef(0);

  // Atualiza as refs quando mudam
  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  useEffect(() => {
    loadIdRef.current += 1;
    const currentLoadId = loadIdRef.current;
    setComponent(null);
    setError(null);
    setLoading(true);

    const loadComponent = async () => {
      try {
        if (onLoadingChangeRef.current) {
          onLoadingChangeRef.current(true);
        }
        
        console.log(`[ExternalPageLoader] Loading component: ${componentPath} from container: ${containerName} (URL: ${moduleUrl})`);
        
        await ensureScriptLoaded(moduleUrl);
        
        // Aguarda um pouco mais para garantir que o container está disponível
        await new Promise(resolve => setTimeout(resolve, 200));

        const module = await dynamicFederation(containerName, componentPath);
        const Component = module.default || module;
        
        if (!Component) {
          throw new Error(`Component ${componentPath} is null or undefined`);
        }
        
        // Só atualiza estado se esta carga ainda for a atual (evita loading "fora de contexto" ao trocar de página)
        if (currentLoadId !== loadIdRef.current) {
          return;
        }
        
        console.log(`[ExternalPageLoader] Successfully loaded component: ${componentPath} from container: ${containerName}`);
        setComponent(() => Component);
        setLoading(false);
        if (onLoadingChangeRef.current) {
          onLoadingChangeRef.current(false);
        }
      } catch (err) {
        if (currentLoadId !== loadIdRef.current) {
          return;
        }
        console.error(`[ExternalPageLoader] Error loading external component ${componentPath} from container ${containerName} (URL: ${moduleUrl}):`, err);
        setError(err);
        setLoading(false);
        if (onLoadingChangeRef.current) {
          onLoadingChangeRef.current(false);
        }
      }
    };

    loadComponent();
  }, [moduleUrl, containerName, componentPath]);

  if (error) {
    return <div>Error loading component: {error.message}</div>;
  }

  if (!Component) {
    return null;
  }
  return <Component {...props}>{children} </Component>;
};

ExternalPageLoader.propTypes = {
  moduleUrl: PropTypes.string.isRequired,
  containerName: PropTypes.string.isRequired,
  componentPath: PropTypes.string.isRequired,
  children: PropTypes.node,
  props: PropTypes.object,
  onLoadingChange: PropTypes.func,
};

export default ExternalPageLoader;

